import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Check, Clear, Edit, KeyboardArrowDown, KeyboardArrowUp, Search} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  Input,
  InputAdornment,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {TableVirtuoso} from "react-virtuoso";
import CustomEmpty from "../custom-empty";
import "./style.css";

let searchTimeoutID = null;
const SEARCH_TIMEOUT = 100;

const ActionsCell = React.memo(({actions, row, index, editMode}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <TableCell
      className="material-table__actions-cell"
      style={{color: "inherit", width: "1px", whiteSpace: "nowrap", textAlign: "right"}}
    >
      {actions
        .map(a => (typeof a === "function" ? a(row, index) : a))
        .filter(a => a !== null && a !== undefined)
        .map((a, ai) =>
          a.isDropdown ? (
            <Fragment key={`dropdown-${ai}`}>
              <Tooltip key={`key-tooltip-${ai}`} title={a?.tooltip}>
                <span style={{display: "inline-flex", verticalAlign: "middle"}}>
                  <IconButton
                    className={a?.className}
                    color="inherit"
                    disabled={editMode || a?.disabled}
                    onClick={e => setAnchorEl(e.currentTarget)}
                    size="small"
                  >
                    {a?.icon}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                {a.items
                  .filter(item => item !== null && item !== undefined)
                  .map((item, k) =>
                    item.onClick ? (
                      <MenuItem
                        disabled={item.disabled}
                        className={item.className}
                        key={`menu-item-key-${k}`}
                        onClick={e => {
                          handleClose();
                          item.onClick(e, row, index);
                        }}
                      >
                        {item.icon ? <ListItemIcon sx={{color: "inherit"}}>{item.icon}</ListItemIcon> : null}
                        {item.label}
                      </MenuItem>
                    ) : (
                      <ListSubheader key={`menu-item-key-${k}`} className={item?.className}>
                        {item.label}
                      </ListSubheader>
                    )
                  )}
              </Menu>
            </Fragment>
          ) : (
            <Tooltip key={`key-tooltip-${ai}`} title={a?.tooltip}>
              <span style={{display: "inline-flex", verticalAlign: "middle"}}>
                <IconButton
                  className={a?.className}
                  color="inherit"
                  disabled={editMode || a?.disabled}
                  onClick={e => {
                    e.stopPropagation();
                    a?.onClick(e, row, index);
                  }}
                  size="small"
                >
                  {a?.icon}
                </IconButton>
              </span>
            </Tooltip>
          )
        )}
    </TableCell>
  );
});

const ItemContent = ({
  t,
  idKey,
  currentRow,
  actions,
  index,
  row,
  columns,
  editMode,
  editableIndex,
  onConfirm,
  onChange,
  setEditMode,
  setEditableIndex,
  setCurrentRow,
  checkedKeys,
  onCheck,
  compact
}) => (
  <>
    {checkedKeys && onCheck && idKey && (
      <TableCell padding="checkbox">
        <Tooltip
          title={
            checkedKeys.includes(row[idKey])
              ? t("components.materialTable.checkbox.uncheck.tooltip")
              : t("components.materialTable.checkbox.check.tooltip")
          }
        >
          <Checkbox
            sx={{padding: compact ? "8px" : undefined}}
            checked={checkedKeys.includes(row[idKey])}
            onChange={() => onCheck(row[idKey])}
            slotProps={{
              input: {
                ["aria-label"]: checkedKeys.includes(row[idKey])
                  ? t("components.materialTable.checkbox.uncheck.tooltip")
                  : t("components.materialTable.checkbox.check.tooltip")
              }
            }}
          />
        </Tooltip>
      </TableCell>
    )}
    {columns
      .filter(column => !column.hidden)
      .map((column, c, filteredColumns) => (
        <Tooltip key={`key-${c}`} title={column.renderTooltip ? column.renderTooltip(row) : ""}>
          <TableCell
            className={column?.getClassName ? column.getClassName(row) : undefined}
            style={{
              color: "inherit",
              width: `${column.width ? column.width + "px" : 100 / filteredColumns.filter(col => !col.width).length + "%"}`,
              minWidth: column.width ? column.width : undefined,
              maxWidth: column.width ? column.width : undefined,
              padding: compact ? "8px" : undefined,
              height: compact ? "40px" : undefined
            }}
          >
            {index === editableIndex && !column.editDisabled ? (
              <Input
                key={`key-${c}-${column.field}`}
                value={currentRow[column.field]}
                name={column.field}
                onChange={e => onChange(e)}
              />
            ) : column.render ? (
              <div style={{wordBreak: "break-all", textWrap: "wrap"}}>{column.render(row)}</div>
            ) : (
              <div style={{wordBreak: "break-all", textWrap: "wrap"}}>{row[column.field]}</div>
            )}
          </TableCell>
        </Tooltip>
      ))}
    {index !== editableIndex ? (
      <ActionsCell actions={actions} row={row} index={index} editMode={editMode} />
    ) : (
      <TableCell style={{color: "inherit", width: "1%", whiteSpace: "nowrap"}}>
        <Tooltip title={t("commons.confirm.save")}>
          <IconButton
            className="material-table__edit-actions__confirm"
            color="primary"
            onClick={e => {
              e.stopPropagation();
              onConfirm(row);
            }}
            size="small"
          >
            <Check />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("commons.confirm.cancel")}>
          <IconButton
            className="material-table__edit-actions__cancel"
            color="primary"
            onClick={e => {
              e.stopPropagation();
              setEditMode(false);
              setEditableIndex(undefined);
              setCurrentRow(undefined);
            }}
            size="small"
          >
            <Clear />
          </IconButton>
        </Tooltip>
      </TableCell>
    )}

    {/* *************** WIP ottimizzazione render cella azioni ********************* */}
    {/* index !== editableIndex && <TableCell style={{ width: '1px', whiteSpace: 'nowrap' }} ><div style={{display:'flex'}}>
        {actions?.map((a, ai) => {
            if (typeof a === "function") {
                return <Tooltip key={`key-tooltip-${ai}`} title={a(row, index)?.tooltip} ><button 
                className='button-action'  
                key={`key-${ai}`} disabled={editMode || a(row, index)?.disabled} onClick={(e) => { e.stopPropagation(); a(e, row, index)?.onClick(e, row, index); }} >
                    {a(row, index)?.icon}</button></Tooltip>
            } else {
                return <Tooltip key={`key-tooltip-${ai}`} title={a?.tooltip}><span><IconButton color="primary" key={`key-${ai}`} disabled={editMode || a?.disabled} onClick={(e) => { e.stopPropagation(); a?.onClick(e, row, index); }} size="small">
                    {a?.icon}</IconButton></span></Tooltip>
            }
        })}
    </div></TableCell>*/}
  </>
);

const MaterialTable = ({
  id,
  idKey = undefined,
  data = [],
  columns = [],
  actions = [],
  rightActions = [],
  additionalToolbarContent,
  onRowClick = null,
  onRowEdit = null,
  getRowStyle = undefined,
  hideHeader = false,
  hideSearch = false,
  compact = false,
  selectedRows,
  onRowSelection
}) => {
  const {t} = useTranslation();

  const toolbarRef = useRef(null);
  const searchRef = useRef(null);

  const [dataRows, setDataRows] = useState();

  const [editMode, setEditMode] = useState(false);
  const [editableIndex, setEditableIndex] = useState();
  const [currentRow, setCurrentRow] = useState();
  const [toolbarHeight, setToolbarHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setToolbarHeight(toolbarRef?.current?.offsetHeight);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const editAction = {
      icon: <Edit />,
      tooltip: t("commons.confirm.edit"),
      onClick: (_, row, index) => {
        setCurrentRow({...row});
        setEditableIndex(index);
        setEditMode(true);
      },
      className: "material-table__edit-actions__edit"
    };

    if (onRowEdit) {
      actions?.unshift(editAction);
    }
  }, [onRowEdit, actions, t]);

  const getFilteredData = useCallback(
    data => {
      const searchedText = searchRef?.current?.value || "";
      if (searchedText.length > 0) {
        const filteredData = (data ?? []).filter(row => {
          for (const [key, value] of Object.entries(row)) {
            const col = columns.find(column => column.field === key);
            if (col) {
              const cellValue = col.renderText ? col.renderText(row) : col.render ? col.render(row) : value;
              if (typeof cellValue === "string" && cellValue.toLowerCase().includes(searchedText.toLowerCase())) {
                return true;
              }
            }
          }
          return false;
        });
        return filteredData;
      } else {
        return data;
      }
    },
    [columns]
  );

  useEffect(() => {
    const filteredData = getFilteredData(data);
    setDataRows(filteredData);
  }, [data, getFilteredData]);

  const onSearchTextChange = () => {
    if (searchTimeoutID) {
      clearTimeout(searchTimeoutID);
    }
    searchTimeoutID = setTimeout(() => {
      const filteredData = getFilteredData(data);
      setDataRows(filteredData);
    }, SEARCH_TIMEOUT);
  };

  const onRowChange = useCallback(e => {
    const value = e.target.value;
    const name = e.target.name;
    setCurrentRow(currentRow => ({...currentRow, [name]: value}));
  }, []);

  const onConfirm = useCallback(
    row => {
      onRowEdit(currentRow, row, editableIndex);
      setCurrentRow(undefined);
      setEditMode(false);
      setEditableIndex(undefined);
    },
    [currentRow, editableIndex, onRowEdit]
  );

  const onRowSelect = useCallback(
    row => {
      const newSelectedRows = selectedRows.includes(row) ? selectedRows.filter(r => r !== row) : [...selectedRows, row];
      onRowSelection(newSelectedRows);
    },
    [selectedRows, onRowSelection]
  );

  const onSelectDeselectAll = useCallback(() => {
    const newSelectedRows = dataRows.length !== selectedRows.length ? dataRows.map(row => row[idKey]) : [];
    onRowSelection(newSelectedRows);
  }, [dataRows, idKey, selectedRows, onRowSelection]);

  const TableComponents = useMemo(() => {
    return {
      Scroller: TableContainer,
      Table: Table,
      TableHead: TableHead,
      TableRow: React.forwardRef((props, ref) => {
        return (
          <TableRow
            {...props}
            key={props["data-index"]}
            innerref={ref}
            id={`material-table__rows__${idKey ? props.item[idKey] : props["data-index"]}`}
            className={
              "v-table-row" + (editMode ? " v-table-row--disabled" : "") + (onRowClick ? " v-table-row--clickable" : "")
            }
            role="row"
            tabIndex={0}
            onClick={e => {
              if (onRowClick) {
                onRowClick(e, props.item, props["data-index"]);
              }
            }}
            onKeyDown={e => {
              if ((e.key === "Enter" || e.key === " ") && onRowClick) {
                e.preventDefault();
                onRowClick(e, props.item, props["data-index"]);
              }
            }}
            sx={getRowStyle ? getRowStyle(props.item) : undefined}
          />
        );
      }),
      TableBody: TableBody
    };
  }, [onRowClick, editMode, idKey, getRowStyle]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div id={id} style={{height: "100%"}}>
      <div id={"material-table__toolbar"} style={{display: "flex", alignItems: "center"}} ref={toolbarRef}>
        {additionalToolbarContent}
        <div style={{flexGrow: 1}}>
          {!hideSearch && (
            <TextField
              inputRef={searchRef}
              id={`material-table__search`}
              fullWidth
              placeholder={t("components.materialTable.search.placeholder")}
              onChange={onSearchTextChange}
              disabled={editMode}
              slotProps={{
                htmlInput: {
                  "aria-label": t("components.materialTable.search.ariaLabel")
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size="small" />
                    </InputAdornment>
                  ),
                  endAdornment:
                    (searchRef?.current?.value || "").length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={editMode}
                          onClick={() => {
                            searchRef.current.value = "";
                            onSearchTextChange();
                          }}
                        >
                          <Clear color="primary" size="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                }
              }}
            />
          )}
        </div>
        {rightActions && rightActions.length > 0 && (
          <div style={{marginLeft: "0.5rem", marginTop: "0.5rem", display: "flex"}}>
            {rightActions
              .filter(ra => ra !== null && ra !== undefined)
              .map((ra, k) => {
                if (ra.isDropdown) {
                  return (
                    <Fragment key={`dropdown-${k}`}>
                      <Button
                        id={ra?.id}
                        style={{marginLeft: "0.2rem"}}
                        startIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        color="primary"
                        size="small"
                        onClick={e => setAnchorEl(e.currentTarget)}
                        disabled={editMode || ra?.disabled}
                      >
                        {ra?.label}
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right"
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right"
                        }}
                      >
                        {ra.items
                          .filter(item => item !== null && item !== undefined)
                          .map((item, k) => (
                            <MenuItem
                              id={item.id}
                              key={`menu-item-key-${k}`}
                              disabled={item?.disabled}
                              onClick={() => {
                                handleClose();
                                item.onClick();
                              }}
                            >
                              {item.startIcon ? <ListItemIcon>{item.startIcon}</ListItemIcon> : null}
                              {item.label}
                            </MenuItem>
                          ))}
                      </Menu>
                    </Fragment>
                  );
                } else {
                  return (
                    <Button
                      key={`key-${k}`}
                      id={ra?.id}
                      style={{marginLeft: "0.2rem"}}
                      startIcon={ra?.startIcon}
                      color="primary"
                      size="small"
                      onClick={() => ra?.onClick()}
                      disabled={editMode || ra?.disabled}
                    >
                      {ra?.label}
                    </Button>
                  );
                }
              })}
          </div>
        )}
      </div>
      {hideHeader && <Divider sx={{paddingTop: "16px"}} />}
      <div
        id={`material-table__table`}
        style={{height: `calc(${dataRows && dataRows.length > 0 ? 100 : 30}% - ${toolbarHeight}px)`, width: "100%"}}
      >
        <TableVirtuoso
          key={"v-table-virtuoso"}
          data={dataRows}
          components={TableComponents}
          fixedHeaderContent={() => {
            return (
              !hideHeader && (
                <TableRow>
                  {selectedRows && onRowSelection && idKey && (
                    <TableCell
                      padding="checkbox"
                      style={{
                        background: "white"
                      }}
                    >
                      <Tooltip
                        title={
                          dataRows?.length === selectedRows?.length
                            ? t("components.materialTable.checkboxTitle.uncheck.tooltip")
                            : t("components.materialTable.checkboxTitle.check.tooltip")
                        }
                      >
                        <Checkbox
                          checked={dataRows?.length === selectedRows?.length}
                          onChange={() => onSelectDeselectAll()}
                          slotProps={{
                            input: {
                              "aria-label":
                                dataRows?.length === selectedRows?.length
                                  ? t("components.materialTable.checkboxTitle.uncheck.tooltip")
                                  : t("components.materialTable.checkboxTitle.check.tooltip")
                            }
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  {columns
                    .filter(column => !column.hidden)
                    .map((column, j, filteredColumns) => (
                      <TableCell
                        key={`key-${j}`}
                        style={{
                          background: "white",
                          color: "inherit",
                          width: `${
                            column.width
                              ? column.width + "px"
                              : 100 / filteredColumns.filter(col => !col.width).length + "%"
                          }`,
                          minWidth: column.width ? column.width : undefined,
                          maxWidth: column.width ? column.width : undefined,
                          padding: compact ? "16px 8px" : undefined
                        }}
                      >
                        {column.title}
                      </TableCell>
                    ))}
                  {actions ? <TableCell style={{background: "white"}}></TableCell> : null}
                </TableRow>
              )
            );
          }}
          itemContent={(index, row) => (
            <ItemContent
              t={t}
              idKey={idKey}
              currentRow={currentRow}
              actions={actions}
              index={index}
              row={row}
              columns={columns}
              editMode={editMode}
              editableIndex={editableIndex}
              onConfirm={onConfirm}
              onChange={onRowChange}
              setEditMode={setEditMode}
              setEditableIndex={setEditableIndex}
              setCurrentRow={setCurrentRow}
              checkedKeys={selectedRows}
              onCheck={onRowSelect}
              compact={compact}
            />
          )}
        />
        {dataRows?.length < 1 && <CustomEmpty text={t("components.materialTable.empty")} />}
      </div>
    </div>
  );
};

export default MaterialTable;
